/**
 * Display-scope helpers for tenant-owned records.
 * Backend RBAC remains the authorization source of truth; these utilities only
 * normalize ownership fields and apply optional presentation filters.
 */

export type ScopeRecord = Record<string, unknown>

const DEV = process.env.NODE_ENV !== 'production'

function asFiniteNumber(value: unknown): number | undefined {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

function readNestedId(obj: unknown, ...paths: string[]): number | undefined {
  if (obj == null || typeof obj !== 'object') return undefined
  const record = obj as ScopeRecord
  for (const path of paths) {
    const parts = path.split('.')
    let cur: unknown = record
    for (const part of parts) {
      if (cur == null || typeof cur !== 'object') {
        cur = undefined
        break
      }
      cur = (cur as ScopeRecord)[part]
    }
    const id = asFiniteNumber(cur)
    if (id != null) return id
  }
  return undefined
}

/** Extract row array from common Fleet Doctor API response shapes. */
export function extractRows<T>(response: unknown): T[] {
  if (response == null) return []

  if (Array.isArray(response)) return response as T[]

  if (typeof response !== 'object') {
    if (DEV) {
      console.warn('[tenantScopeMatch] extractRows: unsupported primitive response', response)
    }
    return []
  }

  const root = response as ScopeRecord

  // AxiosResponse<T> or AxiosResponse<Page<T>>
  if ('data' in root) {
    return extractRows<T>(root.data)
  }

  if (Array.isArray(root.content)) return root.content as T[]
  if (Array.isArray(root.items)) return root.items as T[]
  if (Array.isArray(root.results)) return root.results as T[]
  if (Array.isArray(root.records)) return root.records as T[]

  if (DEV) {
    console.warn('[tenantScopeMatch] extractRows: unsupported response shape', root)
  }
  return []
}

export function normalizePagedResponse<T>(response: unknown): { rows: T[]; total?: number } {
  const rows = extractRows<T>(response)
  if (response == null || typeof response !== 'object') return { rows }

  const root = response as ScopeRecord
  const payload = 'data' in root && root.data != null ? (root.data as ScopeRecord) : root
  const total = asFiniteNumber(payload.totalElements ?? payload.total ?? payload.count)
  return total != null ? { rows, total } : { rows }
}

export function resolveVesselCompanyGroupId(vessel: object | null | undefined): number | undefined {
  if (!vessel) return undefined
  return readNestedId(
    vessel as ScopeRecord,
    'companyGroupAdminId',
    'companyGroupId',
    'companyGroupAdmin.id',
    'companyAdmin.cga.id',
    'companyAdmin.cgaid.id'
  )
}

export function resolveVesselCompanyAdminId(vessel: object | null | undefined): number | undefined {
  if (!vessel) return undefined
  return readNestedId(vessel as ScopeRecord, 'companyAdminId', 'companyId', 'companyAdmin.id')
}

export function resolveRecordVesselId(record: ScopeRecord): number | undefined {
  return readNestedId(
    record,
    'vesselId',
    'vessel.id',
    'crew.vesselId',
    'crew.vessel.id',
    'currentAssignment.vesselId',
    'currentAssignment.vessel.id'
  )
}

/** Resolve company-group ownership from flat or nested record/vessel shapes. */
export function resolveRecordCompanyGroupId(
  record: ScopeRecord,
  vesselById?: ReadonlyMap<number, ScopeRecord>
): number | undefined {
  const direct = readNestedId(
    record,
    'companyGroupAdminId',
    'CompanyGroupAdminId',
    'companyGroupAdmin.id',
    'crew.companyGroupAdminId',
    'crew.companyGroupAdmin.id',
    'crew.CompanyGroupAdminId'
  )
  if (direct != null) return direct

  const vesselId = resolveRecordVesselId(record)
  if (vesselId != null && vesselById?.has(vesselId)) {
    return resolveVesselCompanyGroupId(vesselById.get(vesselId))
  }

  return readNestedId(record, 'vessel.companyGroupAdminId', 'vessel.companyGroupAdmin.id')
}

/** Resolve company-admin ownership; null companyAdminId on CGA-owned rows is valid. */
export function resolveRecordCompanyAdminId(
  record: ScopeRecord,
  vesselById?: ReadonlyMap<number, ScopeRecord>
): number | undefined {
  const direct = readNestedId(
    record,
    'companyAdminId',
    'companyAdmin.id',
    'crew.companyAdminId',
    'crew.companyAdmin.id'
  )
  if (direct != null) return direct

  const vesselId = resolveRecordVesselId(record)
  if (vesselId != null && vesselById?.has(vesselId)) {
    return resolveVesselCompanyAdminId(vesselById.get(vesselId))
  }

  return readNestedId(record, 'vessel.companyAdminId', 'vessel.companyAdmin.id')
}

export function matchesCompanyGroupScope(
  record: ScopeRecord,
  companyGroupId: number | undefined | null | '',
  vesselById?: ReadonlyMap<number, ScopeRecord>
): boolean {
  if (companyGroupId == null || companyGroupId === '') return true
  const target = Number(companyGroupId)
  if (!Number.isFinite(target)) return true
  const resolved = resolveRecordCompanyGroupId(record, vesselById)
  // Trust backend-scoped payload when ownership metadata is absent on the row.
  if (resolved == null) return true
  return resolved === target
}

export function matchesCompanyAdminScope(
  record: ScopeRecord,
  companyAdminId: number | undefined | null | '',
  vesselById?: ReadonlyMap<number, ScopeRecord>
): boolean {
  if (companyAdminId == null || companyAdminId === '') return true
  const target = Number(companyAdminId)
  if (!Number.isFinite(target)) return true
  const resolved = resolveRecordCompanyAdminId(record, vesselById)
  if (resolved == null) return true
  return resolved === target
}

export function matchesVesselScope(
  record: ScopeRecord,
  vesselId: number | undefined | null | ''
): boolean {
  if (vesselId == null || vesselId === '') return true
  const target = Number(vesselId)
  if (!Number.isFinite(target)) return true
  const resolved = resolveRecordVesselId(record)
  if (resolved == null) return true
  return resolved === target
}

/** Superadmin / fleet-wide operator apply company presentation filters; tenant roles trust backend. */
export function shouldApplyTenantCompanyFilters(opts: {
  isSuperAdmin: boolean
  effectiveBehavior?: string
  isGlobalScopeOperator?: boolean
}): boolean {
  if (opts.isSuperAdmin) return true
  const behavior = String(opts.effectiveBehavior ?? '').toUpperCase()
  if (behavior === 'SUPER_ADMIN') return true
  if (behavior === 'OPERATOR' && opts.isGlobalScopeOperator) return true
  return false
}

export type PresentationScopeFilterInput = {
  record: ScopeRecord
  vesselById?: ReadonlyMap<number, ScopeRecord>
  applyCompanyFilters: boolean
  fixedCompanyGroupId?: number
  selectedCompanyGroupId?: number | ''
  fixedCompanyAdminId?: number
  selectedCompanyAdminId?: number | ''
  selectedVesselId?: number | ''
}

/** Optional presentation narrowing — never re-implements tenant authorization. */
export function passesPresentationScopeFilters(input: PresentationScopeFilterInput): boolean {
  const {
    record,
    vesselById,
    applyCompanyFilters,
    fixedCompanyGroupId,
    selectedCompanyGroupId,
    fixedCompanyAdminId,
    selectedCompanyAdminId,
    selectedVesselId,
  } = input

  if (!matchesVesselScope(record, selectedVesselId)) return false

  if (!applyCompanyFilters) return true

  const groupTarget =
    fixedCompanyGroupId ??
    (selectedCompanyGroupId !== '' && selectedCompanyGroupId != null
      ? Number(selectedCompanyGroupId)
      : undefined)
  const adminTarget =
    fixedCompanyAdminId ??
    (selectedCompanyAdminId !== '' && selectedCompanyAdminId != null
      ? Number(selectedCompanyAdminId)
      : undefined)

  if (groupTarget != null && !matchesCompanyGroupScope(record, groupTarget, vesselById)) return false
  if (adminTarget != null && !matchesCompanyAdminScope(record, adminTarget, vesselById)) return false

  return true
}

/** Filter vessels for dropdown UX; uses flat + nested ownership ids. */
export function filterVesselsForPresentationScope<T extends object>(
  vessels: T[],
  opts: {
    companyGroupId?: number
    companyAdminId?: number
    vesselId?: number
    activeOnly?: boolean
  }
): T[] {
  const activeOnly = opts.activeOnly ?? true
  return vessels.filter((vessel) => {
    const v = vessel as unknown as ScopeRecord
    if (activeOnly && v.active === false) return false
    if (opts.vesselId != null && Number(v.id) !== opts.vesselId) return false
    if (opts.companyAdminId != null) {
      return resolveVesselCompanyAdminId(v) === opts.companyAdminId
    }
    if (opts.companyGroupId != null) {
      return resolveVesselCompanyGroupId(v) === opts.companyGroupId
    }
    return true
  })
}

/** Vessel list row visible after backend scope (presentation-only active filter). */
export function vesselVisibleForTenantRole(
  vessel: ScopeRecord,
  opts: {
    effectiveBehavior?: string
    companyGroupAdminId?: number | null
    companyAdminId?: number | null
    isGlobalScopeOperator?: boolean
  }
): boolean {
  const isActive = vessel.active !== false
  if (!isActive) return false

  const behavior = String(opts.effectiveBehavior ?? '').toUpperCase()
  if (behavior === 'SUPER_ADMIN' || opts.isGlobalScopeOperator) return true

  if (behavior === 'COMPANY_GROUP_ADMIN' || (behavior === 'OPERATOR' && opts.companyGroupAdminId != null)) {
    const cgaId = opts.companyGroupAdminId
    return cgaId == null || resolveVesselCompanyGroupId(vessel) === Number(cgaId)
  }

  if (behavior === 'COMPANY_ADMIN') {
    const caId = opts.companyAdminId
    return caId == null || resolveVesselCompanyAdminId(vessel) === Number(caId)
  }

  return true
}
